/**
 * Display reason for cart tool actions
 * Used by UI to show appropriate chip text without fragile string matching
 */
export type CartToolDisplayReason =
  | 'keep_cart'
  | 'clear_cart'
  | 'conflict_check'
  | 'switch_employee'
  | 'add_products'
  | 'navigate_to_cart'
  | 'add_to_existing';

/**
 * Constants for cart tool display reasons
 * Use these instead of string literals to ensure consistency and type safety
 */
export const CART_TOOL_DISPLAY_REASONS = {
  KEEP_CART: 'keep_cart' as const,
  CLEAR_CART: 'clear_cart' as const,
  CONFLICT_CHECK: 'conflict_check' as const,
  SWITCH_EMPLOYEE: 'switch_employee' as const,
  ADD_PRODUCTS: 'add_products' as const,
  NAVIGATE_TO_CART: 'navigate_to_cart' as const,
  ADD_TO_EXISTING: 'add_to_existing' as const,
} satisfies Record<string, CartToolDisplayReason>;

/**
 * Constants for conflict resolution strategies
 * Use these instead of string literals to ensure consistency and type safety
 */
export const CONFLICT_RESOLUTION = {
  KEEP_CART: 'keep_cart' as const,
  CLEAR_CART: 'clear_cart' as const,
  NAVIGATE_TO_CART: 'navigate_to_cart' as const,
  ADD_TO_EXISTING: 'add_to_existing' as const,
} as const;

export type ConflictResolution =
  (typeof CONFLICT_RESOLUTION)[keyof typeof CONFLICT_RESOLUTION];

/**
 * Tool name constants for type-safe tool references
 */
export const TOOL_NAMES = {
  NAVIGATE_TO_PAGE: 'navigate_to_page',
  ADD_PRODUCTS_TO_CART: 'add_products_to_cart',
  CONNECT_APP_INTEGRATION: 'connect_app_integration',
  CONNECT_HR_SYSTEM: 'connect_hr_system',
  OPEN_SUPPORT_TICKET: 'open_support_ticket',
  MARK_GS_STEP_COMPLETE: 'mark_getting_started_step_complete',
  IMPORT_EMPLOYEES_CSV: 'import_employees_csv',
  // Internal tools that should be filtered from UI
  SELECT_TOOLSET: 'select_toolset',
  SELECT_TOOL_SET: 'select_tool_set',
} as const;

/**
 * CopilotKit tool execution status values
 * Used in render functions to determine the current state of a tool call
 */
export const COPILOT_STATUS = {
  IN_PROGRESS: 'inProgress',
  EXECUTING: 'executing',
  COMPLETE: 'complete',
} as const;

export type CopilotStatus =
  (typeof COPILOT_STATUS)[keyof typeof COPILOT_STATUS];
